import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, PlusCircle, MessageCircle, User, Store, Briefcase, CalendarDays, Wrench, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMessages } from '../contexts/MessagesContext';

/**
 * Persistent 5-icon bottom navigation for mobile (md:hidden).
 * Increases session time by making core routes always one tap away.
 * Hidden inside the listing detail page (where MobileQuickActions takes over).
 */
const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { unreadCount: unread } = useMessages();
  const [searchOpen, setSearchOpen] = useState(false);

  // Suppress on routes that own the bottom slot (listing detail's
  // MobileQuickActions bar) or on auth flow pages where a bottom nav
  // would distract from the primary CTA.
  const HIDE_ON = ['/listing/', '/login', '/register', '/mfa-verify', '/verify-email', '/reset-password', '/forgot-password', '/create-listing', '/list-your-business'];
  const shouldHide = HIDE_ON.some(p => location.pathname.startsWith(p));

  if (shouldHide) return null;

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const items = [
    { to: '/', label: 'Home', icon: Home, active: isActive('/', true), testid: 'mnav-home' },
    { onClick: () => setSearchOpen(true), label: 'Search', icon: Search, active: isActive('/businesses') || isActive('/search') || isActive('/jobs') || isActive('/events') || isActive('/marketplace') || isActive('/find-a-trade'), testid: 'mnav-search' },
    { to: user ? '/create-listing' : '/login', label: 'Post', icon: PlusCircle, active: false, primary: true, testid: 'mnav-post' },
    { to: user ? '/messages' : '/login', label: 'Messages', icon: MessageCircle, active: isActive('/messages'), badge: unread, testid: 'mnav-messages' },
    { to: user ? '/dashboard' : '/login', label: user ? 'Me' : 'Login', icon: User, active: isActive('/dashboard') || isActive('/profile'), testid: 'mnav-me' },
  ];

  const searchOptions = [
    { to: '/businesses', label: 'Businesses', desc: 'Local shops & services', icon: Store, testid: 'search-sheet-businesses' },
    { to: '/jobs', label: 'Jobs', desc: 'Roles hiring in Essex', icon: Briefcase, testid: 'search-sheet-jobs' },
    { to: '/events', label: 'Events', desc: "What's on this week", icon: CalendarDays, testid: 'search-sheet-events' },
    { to: '/find-a-trade', label: 'Find a Trade', desc: 'Get quotes from tradespeople', icon: Wrench, testid: 'search-sheet-trades' },
  ];

  const handleSearchSheetSelect = (to) => { setSearchOpen(false); navigate(to); };

  return (
    <>
      {/* Spacer so page content is never occluded by the nav */}
      <div className="h-16 md:hidden" aria-hidden="true" />

      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(15,44,62,0.06)] pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-nav"
        role="navigation"
        aria-label="Primary mobile navigation"
      >
        <div className="grid grid-cols-5 h-16">
          {items.map((it) => {
            const Icon = it.icon;
            if (it.primary) {
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={it.testid}
                  className="relative flex items-center justify-center"
                >
                  <span className="absolute -top-4 flex items-center justify-center w-14 h-14 rounded-full bg-[#D4A017] text-slate-900 shadow-lg ring-4 ring-white hover:bg-[#c39115] transition-colors">
                    <Icon className="h-6 w-6" />
                  </span>
                  <span className="absolute bottom-1.5 text-[10px] font-medium text-slate-600">{it.label}</span>
                </Link>
              );
            }
            return (
              it.onClick ? (
                <button
                  key={it.label}
                  type="button"
                  onClick={it.onClick}
                  data-testid={it.testid}
                  className={`flex flex-col items-center justify-center gap-0.5 relative text-[11px] transition-colors ${
                    it.active ? 'text-[#4A6741] font-medium' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${it.active ? 'text-[#4A6741]' : ''}`} />
                  <span>{it.label}</span>
                </button>
              ) : (
                <Link
                  key={it.to + it.label}
                  to={it.to}
                  data-testid={it.testid}
                  className={`flex flex-col items-center justify-center gap-0.5 relative text-[11px] transition-colors ${
                    it.active ? 'text-[#4A6741] font-medium' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${it.active ? 'text-[#4A6741]' : ''}`} />
                  <span>{it.label}</span>
                  {it.badge > 0 && (
                    <span className="absolute top-1.5 right-4 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full" data-testid={`${it.testid}-badge`}>
                      {it.badge > 9 ? '9+' : it.badge}
                    </span>
                  )}
                </Link>
              )
            );
          })}
        </div>
      </nav>

      {/* Search chooser — bottom sheet that lets the user pick which
          directory to browse instead of hard-routing to /businesses */}
      {searchOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="search-sheet-backdrop" onClick={() => setSearchOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] shadow-2xl animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Search Discover Essex"
            data-testid="search-sheet"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Search Discover Essex</h3>
              <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search chooser" data-testid="search-sheet-close" className="p-1.5 rounded-md hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {searchOptions.map((o) => {
                const Icon = o.icon;
                return (
                  <button
                    key={o.to}
                    type="button"
                    onClick={() => handleSearchSheetSelect(o.to)}
                    data-testid={o.testid}
                    className="flex flex-col items-start gap-1.5 p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-[#4A6741] transition-colors text-left"
                  >
                    <span className="w-10 h-10 rounded-lg bg-[#4A6741]/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[#4A6741]" />
                    </span>
                    <span className="font-semibold text-slate-900 text-sm">{o.label}</span>
                    <span className="text-xs text-slate-500 leading-tight">{o.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileBottomNav;
